import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { SocialProvider } from '../../api/userland'
import { cleanReferralCode, readReferredBy, writeReferredBy } from '../../lib/referredBy'
import { writeSocialPending } from '../../lib/socialPending'
import {
  isTelegramWebView,
  socialResumeUrl,
  tryOpenExternalBrowser,
} from '../../lib/telegramWebView'
import soft from '../../styles/soft.module.css'
import { useAuthShell } from '../../wallet/AuthShellContext'
import { AuthHero } from './AuthHero'
import styles from './AuthShell.module.css'

function isSocialProvider(value: string | null): value is SocialProvider {
  return value === 'google' || value === 'telegram'
}

export function AuthContinuePage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { openAuth } = useAuthShell()
  const ran = useRef(false)
  const provider = params.get('provider')
  const ticket = params.get('ticket')?.trim() || ''
  // The referral code that crossed the WebView → browser hop (socialResumeUrl).
  const ref = cleanReferralCode(params.get('ref'))

  useEffect(() => {
    if (ran.current) return
    ran.current = true
    if (ref) writeReferredBy(ref)
    if (!isSocialProvider(provider)) {
      navigate('/discover', { replace: true })
      return
    }
    if (isTelegramWebView()) {
      return
    }
    if (ticket) writeSocialPending({ provider, ticket })
    openAuth(ticket ? 'ton-recovery' : 'chooser')
    navigate('/discover', { replace: true })
  }, [navigate, openAuth, provider, ref, ticket])

  if (!isSocialProvider(provider) || !isTelegramWebView()) {
    return (
      <div className={styles.sheet} style={{ margin: '12vh auto' }}>
        <div className={styles.chooser}>
          <p className={styles.lead}>Opening Wils.trade…</p>
        </div>
      </div>
    )
  }

  const resume = socialResumeUrl(
    window.location.origin,
    provider,
    ticket || undefined,
    ref ?? readReferredBy()
  )
  return (
    <div className={styles.sheet} style={{ margin: '12vh auto' }}>
      <ExternalBrowserCard
        resumeUrl={resume}
        hasTicket={Boolean(ticket)}
        onBack={() => navigate('/discover', { replace: true })}
      />
    </div>
  )
}

export function ExternalBrowserCard({
  resumeUrl,
  hasTicket,
  onBack,
}: {
  resumeUrl: string
  hasTicket: boolean
  onBack?: () => void
}) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    const ok = await copyText(resumeUrl)
    if (ok) {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    }
  }
  const open = () => {
    if (tryOpenExternalBrowser(resumeUrl)) return
    const tab = window.open(resumeUrl, '_blank', 'noopener,noreferrer')
    if (tab) return
    void copy()
  }
  return (
    <div className={styles.chooser}>
      <AuthHero kind="browser" />
      <h2 className={styles.welcome}>Open in Safari or Chrome</h2>
      <p className={styles.lead}>
        Telegram’s in-app browser cannot create a trading wallet. Open this page in Safari or
        Chrome, then continue.
      </p>
      <p className={styles.lead}>
        iPhone: tap ⋯ then Open in Safari. Android: ⋯ then Open in Chrome.
      </p>
      {hasTicket ? (
        <p className={styles.lead}>Your Telegram login is saved for ten minutes.</p>
      ) : null}
      <button type="button" className={`${soft.action} ${styles.primary}`} onClick={open}>
        Open in browser
      </button>
      <button
        type="button"
        className={`${soft.btn} ${styles.method} ${styles.methodCentre}`}
        onClick={() => void copy()}
      >
        {copied ? 'Copied' : 'Copy link'}
      </button>
      {onBack ? (
        <button
          type="button"
          className={`${soft.btn} ${styles.method} ${styles.methodCentre}`}
          onClick={onBack}
        >
          Back
        </button>
      ) : null}
    </div>
  )
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // fall through
  }
  try {
    const area = document.createElement('textarea')
    area.value = value
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(area)
    return ok
  } catch {
    return false
  }
}
